import { Interview } from "../Entities/Interview";
import { SimSessionWithReport } from "../Entities/SimSession";
import { EventDispatcher } from "../Utils/event-dispatcher";
import apiClient from "./api.client.service";
import jobApplicationService from "./job.application.service";
import AuthService from "./auth.service";
import { InterviewRoundsDTO } from "../Entities/InterviewRounds";
import { InterviewRound } from "../Dto/GenerateJobDescription.dto";
import { FindAllInterviewSchedulesFilters, PaginationType } from "../Interface";

class InterviewService extends EventDispatcher {
  private APIBaseUrl: string;
  private allInterviews: Interview[] = [];
  private scheduledInterviews: Interview[] = [];
  private limit = 25;
  private dispatcher = new EventDispatcher();
  static instance: InterviewService;
  
  // Cache for interview rounds
  private roundsCache: Map<string, { data: InterviewRound; timestamp: number }> = new Map();
  private allRoundsCache: { data: InterviewRound[]; timestamp: number } | null = null;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor() {
    super();
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
    AuthService.getInstance().subscribe(
      AuthService.EVENTS.USER_SIGNED_OUT,
      () => {
        this.clearInterviewServiceData();
      }
    );
  }

  static getInstance(): InterviewService {
    if (!InterviewService.instance) {
      InterviewService.instance = new InterviewService();
    }
    return InterviewService.instance;
  }

  async getInterviewById(interviewId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview/${interviewId}`
      );
      return response.data;
    } catch (error) {
      console.error("🚀 ~ InterviewService ~ getInterviewById ~ error:", error);
      return null;
    }
  }

  async createInterviewRounds(interviewRounds: InterviewRoundsDTO[]) {
    console.log(
      "🚀 ~ InterviewService ~ createInterRounds ~ interviewRounds:",
      interviewRounds
    );
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/interview/rounds`,
        interviewRounds
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ createInterRounds ~ error:",
        error
      );
    }
  }

  async createInterviewRound(interviewRound: InterviewRoundsDTO) {
    console.log(
      "🚀 ~ InterviewService ~ createInterviewRound ~ interviewRound:",
      interviewRound
    );
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/interview/round`,
        interviewRound
      );
      const createdRound = response.data;
      
      if (createdRound && createdRound._id) {
        // Normalize to InterviewRound format
        const normalizedRound = this.normalizeRoundToInterviewRound(createdRound);
        
        // Ensure _id exists after normalization
        if (normalizedRound._id) {
          // Update individual cache
          this.roundsCache.set(normalizedRound._id, {
            data: normalizedRound,
            timestamp: Date.now(),
          });
          
          // Add to all rounds cache WITHOUT full invalidation
          if (this.allRoundsCache) {
            const existingIndex = this.allRoundsCache.data.findIndex(
              (r) => r._id === normalizedRound._id
            );
            
            if (existingIndex >= 0) {
              // Update existing (shouldn't happen for new rounds, but safety check)
              this.allRoundsCache.data[existingIndex] = normalizedRound;
            } else {
              // Add new round to cache
              this.allRoundsCache.data.push(normalizedRound);
              // Re-deduplicate to be safe
              this.allRoundsCache.data = this.deduplicateTemplates(
                this.allRoundsCache.data
              );
            }
            
            // Update timestamp
            this.allRoundsCache.timestamp = Date.now();
          }
          
          // Dispatch event with normalized round
          this.dispatch('roundCreated', normalizedRound);
          
          return normalizedRound; // Return normalized format
        }
      }
      
      return createdRound;
    } catch (error: any) {
      console.error(
        "🚀 ~ InterviewService ~ createInterviewRound ~ error:",
        error
      );
      console.error("Request payload:", interviewRound);
      console.error("Error response:", error?.response?.data);
      throw error; // Re-throw to allow caller to handle
    }
  }

  async updateInterviewRound(interviewRounds: InterviewRoundsDTO) {
    console.log(
      "🚀 ~ InterviewService ~ updateInterviewRound ~ interviewRounds:",
      interviewRounds
    );
    try {
      const response = await apiClient.put(
        `${this.APIBaseUrl}/admin/interview/${interviewRounds._id}`,
        interviewRounds
      );
      const updatedRound = response.data;
      
      if (updatedRound && updatedRound._id) {
        // Normalize to InterviewRound format
        const normalizedRound = this.normalizeRoundToInterviewRound(updatedRound);
        
        // Ensure _id exists after normalization
        if (normalizedRound._id) {
          // Update individual cache
          this.roundsCache.set(normalizedRound._id, {
            data: normalizedRound,
            timestamp: Date.now(),
          });
          
          // Update in all rounds cache WITHOUT full invalidation
          if (this.allRoundsCache) {
            const existingIndex = this.allRoundsCache.data.findIndex(
              (r) => r._id === normalizedRound._id
            );
            
            if (existingIndex >= 0) {
              this.allRoundsCache.data[existingIndex] = normalizedRound;
            } else {
              // Round not in cache, add it
              this.allRoundsCache.data.push(normalizedRound);
              this.allRoundsCache.data = this.deduplicateTemplates(
                this.allRoundsCache.data
              );
            }
            
            this.allRoundsCache.timestamp = Date.now();
          }
          
          // Dispatch event with normalized round
          this.dispatch('roundUpdated', normalizedRound);
          
          return normalizedRound; // Return normalized format
        }
      }
      
      return updatedRound;
    } catch (error: any) {
      console.error(
        "🚀 ~ InterviewService ~ updateInterviewRound ~ error:",
        error
      );
      console.error("Request payload:", interviewRounds);
      console.error("Error response:", error?.response?.data);
      throw error; // Re-throw to allow caller to handle
    }
  }

  async deleteInterviewRound(roundId: string) {
    try {
      const response = await apiClient.delete(
        `${this.APIBaseUrl}/admin/interview/${roundId}`
      );
      if (response.status === 200) {
        // Invalidate cache
        this.invalidateRoundsCache(roundId);
        
        // Dispatch event for UI updates
        this.dispatch('roundDeleted', roundId);
        
        return { success: true, message: response.data.message };
      }
    } catch (error: any) {
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to delete interview round",
      };
    }
  }

  async updateRoundNumbers(interviewRounds: InterviewRoundsDTO[]) {
    try {
      const response = await apiClient.put(
        `${this.APIBaseUrl}/admin/interview/update-round-numbers`,
        interviewRounds
      );
      return response.data;
    } catch (error) {
      console.error("Error updating interview round numbers:", error);
      throw error;
    }
  }

  async updateInterviewRounds(interviewRounds: InterviewRoundsDTO[]) {
    console.log(
      "🚀 ~ InterviewService ~ createInterRounds ~ interviewRounds:",
      interviewRounds
    );
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/interview/updateRounds`,
        interviewRounds
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ createInterRounds ~ error:",
        error
      );
    }
  }

  async getAllInterviewRoundsSpecificToJob(
    jobPostId: string
  ): Promise<InterviewRound[]> {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/interview/getRounds`,
        {
          jobPostId: jobPostId,
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ createInterRounds ~ error:",
        error
      );
      return [];
    }
  }

  async getAllInterviewRoundsSpecificToOrganization(forceRefresh: boolean = false): Promise<
    InterviewRound[]
  > {
    // Check cache first
    if (!forceRefresh && this.allRoundsCache && Date.now() - this.allRoundsCache.timestamp < this.CACHE_TTL) {
      return this.allRoundsCache.data;
    }
    
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview/rounds`
      );
      const rawRounds = response.data || [];
      
      // Normalize all rounds to InterviewRound format
      const normalizedRounds = rawRounds.map((round: any) => 
        this.normalizeRoundToInterviewRound(round)
      );
      
      // Deduplicate at service level
      const uniqueRounds = this.deduplicateTemplates(normalizedRounds);
      
      // Update cache
      this.allRoundsCache = {
        data: uniqueRounds,
        timestamp: Date.now(),
      };
      
      // Populate individual round cache for quick lookups
      uniqueRounds.forEach((round: InterviewRound) => {
        if (round._id) {
          this.roundsCache.set(round._id, {
            data: round,
            timestamp: Date.now(),
          });
        }
      });
      
      return uniqueRounds;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ getAllInterviewRoundsSpecificToOrganization ~ error:",
        error
      );
      
      // Return stale cache if available (graceful degradation)
      if (this.allRoundsCache) {
        console.warn('Using stale cache due to API error');
        return this.allRoundsCache.data;
      }
      
      return [];
    }
  }

  /**
   * Get only generic/default interview rounds (profile-match, overall-assessment, job-alignment)
   * This filters the cached results from getAllInterviewRoundsSpecificToOrganization
   * to avoid processing custom rounds that aren't needed in V3 flow.
   * 
   * Benefits:
   * - Uses existing cache (no additional API calls)
   * - Returns only ~3 generic rounds instead of potentially 50+ rounds
   * - Reduces data processing in V3 components
   * 
   * @param forceRefresh - Force refresh the underlying cache
   * @returns Promise<InterviewRound[]> - Only generic rounds (isGeneric: true)
   */
  async getGenericRoundsOnly(forceRefresh: boolean = false): Promise<InterviewRound[]> {
    const allRounds = await this.getAllInterviewRoundsSpecificToOrganization(forceRefresh);
    return allRounds.filter((round: any) => round.isGeneric === true);
  }

  async getInterviewRoundById(roundId: string, forceRefresh: boolean = false): Promise<InterviewRound | null> {
    // Skip API call for special round IDs that don't exist as individual rounds
    if (roundId === 'job-alignment' || roundId === 'profile-match' || roundId === 'overall-assessment') {
      console.warn(`getInterviewRoundById called for special round ${roundId}, returning null`);
      return null;
    }
    
    // Check cache first
    const cached = this.roundsCache.get(roundId);
    if (!forceRefresh && cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }
    
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview/round/${roundId}`
      );
      const round = response.data;
      
      // Update cache
      if (round && round._id) {
        this.roundsCache.set(round._id, {
          data: round,
          timestamp: Date.now(),
        });
      }
      
      return round;
    } catch (error) {
      // Silently handle errors for special rounds or invalid IDs
      // Only log if it's not a 404 (round not found)
      const isNotFound = (error as any)?.response?.status === 404;
      if (!isNotFound) {
        console.error(
          "🚀 ~ InterviewService ~ getInterviewRoundById ~ error:",
          error
        );
      }
      
      // Return stale cache if available (graceful degradation)
      if (cached) {
        console.warn('Using stale cache due to API error');
        return cached.data;
      }
      
      return null;
    }
  }

  async mergeJobApplicationWithInterviews(
    interviewsData: Interview[]
  ): Promise<Interview[]> {
    const allJobApplications =
      await jobApplicationService.getAllJobApplications();
    const jobApplicationPromises = interviewsData.map(
      async (interview: any) => {
        const applicationId = interview.jobApplicationId;
        let jobApplicationDetails = null;
        if (applicationId) {
          jobApplicationDetails = allJobApplications.find(
            (application) => application._id === applicationId
          );
        }
        return {
          ...interview,
          jobApplication: jobApplicationDetails || null, // Replace jobApplicationId with jobApplication data
        };
      }
    );

    const updatedInterviews = await Promise.all(jobApplicationPromises);
    return updatedInterviews;
  }

  async getAllInterviews(page: number) {
    if (this.allInterviews.length > 0) {
      return this.allInterviews;
    }
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview/scheduled?page=${page}&limit=${this.limit}`
      );
      this.allInterviews = response.data;
      this.dispatch("interviewsUpdated", this.allInterviews); // Triggering event
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ getAllInterviewSchedules ~ error:",
        error
      );
    }
  }

  async getScheduledInterviews(page: number) {
    // if (this.scheduledInterviews.length > 0) {
    //   return this.scheduledInterviews;
    // }
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview/scheduled?page=${page}&limit=${this.limit}`
      );
      const currentDate = new Date();
      const futureInterviews = response?.data?.data.filter((interview: any) => {
        return new Date(interview.startTime) >= currentDate;
      });
      const updatedData = await this.mergeJobApplicationWithInterviews(
        futureInterviews
      );
      response.data.data = updatedData;
      this.scheduledInterviews = updatedData;
      this.dispatch("interviewsUpdated", this.scheduledInterviews); // Triggering event
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ getScheduledInterviews ~ error:",
        error
      );
    }
  }

  async getInterviewSchedulesByDate(date: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview/scheduled?date=${date}`
      );
      const updatedData = await this.mergeJobApplicationWithInterviews(
        response.data.data
      );
      return updatedData;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ getInterviewSchedulesByDate ~ error:",
        error
      );
    }
  }

  async getInterviewScheduleByJobApplicationId(jobApplicationId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview/scheduled?jobApplicationId=${jobApplicationId}`
      );
      return response.data?.data[0];
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ getInterviewScheduleByJobApplicationId ~ error:",
        error
      );
    }
  }

  async getSessionByJobApplicationId(jobApplicationId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview?jobApplicationId=${jobApplicationId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ getSessionByjobApplicationId ~ error:",
        error
      );
    }
  }

  async getQuestionsBySessionId(sessionId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview/questions/${sessionId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ getQuestionsBySessionId ~ error:",
        error
      );
    }
  }

  async getSimSessionsByInterviewSessionId(interviewSessionId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/simulation/sessions/by-interview-session?interviewSessionId=${interviewSessionId}`
      );
      return response.data as SimSessionWithReport[];
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ getSimSessionsByInterviewSessionId ~ error:",
        error
      );
      return [];
    }
  }

  async getInterviewDataByFilter(searchQuery: string): Promise<Interview[]> {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/jobapplications?userName=${searchQuery}`
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async updateInterviewSession(sessionId: string, status: string) {
    try {
      const response = await apiClient.patch(
        `${this.APIBaseUrl}/admin/interview/${sessionId}/${status}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ updateInterviewSession ~ error:",
        error
      );
    }
  }

  async updateSessionStatus(
    sessionId: string,
    status: "qualified" | "unqualified"
  ) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/interview/update-session-status`,
        {
          sessionId,
          status,
        }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "🚀 ~ InterviewService ~ updateSessionStatus ~ error:",
        error
      );
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to update session status",
        data: null,
      };
    }
  }

  async getInterviewSchedulesByUserId(userId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview/scheduled?userId=${userId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ InterviewService ~ getInterviewSchedulesByUserId ~ error:",
        error
      );
    }
  }

  async searchInterviewsByNameOrEmail(
    payload: FindAllInterviewSchedulesFilters,
    pagination: PaginationType
  ) {
    const { search, jobPost, date, organizer, status } = payload;
    const { page, limit } = pagination;
    try {
      let params = `?page=${page}&limit=${limit}`;

      if (search) {
        params += `&search=${search}`;
      }
      if (jobPost) {
        params += `&jobPost=${jobPost}`;
      }
      if (date) {
        params += `&date=${date}`;
      }
      if (organizer) {
        params += `&organizer=${organizer}`;
      }
      if (status) {
        params += `&status=${status}`;
      }

      const url = `${this.APIBaseUrl}/admin/interview/find/schedules${params}`;

      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      return null;
    }
  }

  clearAllFilters() {
    console.log("🚀 ~ InterviewService ~ clearAllFilters ~ clearAllFilters:");
    this.dispatcher.dispatch("clearFilters");
  }

  changeInterviewDate(date: string) {
    this.dispatcher.dispatch("dateFiltered", date);
  }

  clearInterviewServiceData() {
    this.scheduledInterviews = [];
    this.invalidateRoundsCache(); // Clear rounds cache on logout
  }
  
  /**
   * Deduplicate templates by ID at service level
   * This ensures all consumers get clean data
   */
  private deduplicateTemplates(templates: InterviewRound[]): InterviewRound[] {
    const uniqueMap = new Map<string, InterviewRound>();
    
    templates.forEach((template) => {
      if (template._id) {
        // Use ID as primary key
        if (!uniqueMap.has(template._id)) {
          uniqueMap.set(template._id, template);
        }
      } else {
        // Fallback: use title + prompt as composite key
        const fallbackKey = `${template.title || ''}_${template.questionGenerationPrompt || ''}`;
        if (!uniqueMap.has(fallbackKey)) {
          uniqueMap.set(fallbackKey, template);
        }
      }
    });
    
    return Array.from(uniqueMap.values());
  }

  /**
   * Transform API response to InterviewRound format
   * Handles both API format (roundName, customRound) and InterviewRound format
   */
  private normalizeRoundToInterviewRound(round: any): InterviewRound {
    // If already in InterviewRound format, return as-is
    if (round.title && round.questionGenerationPrompt) {
      return round as InterviewRound;
    }
    
    // Transform from API format to InterviewRound format
    return {
      _id: round._id,
      title: round.roundName || round.title || '',
      description: round.description || '',
      questionGenerationPrompt: round.customRound?.custom_ai_prompt || round.questionGenerationPrompt || '',
      evaluationPrompt: round.customRound?.evaluation_criteria || round.evaluationPrompt || '',
      answerType: round.customRound?.question_type || round.answerType || 'mcq',
      category: round.category || ['Custom'],
      type: round.type,
      roundType: round.roundType,
    };
  }
  
  /**
   * Invalidate rounds cache
   * @param roundId - Optional specific round ID to invalidate, if not provided invalidates all
   */
  invalidateRoundsCache(roundId?: string) {
    if (roundId) {
      this.roundsCache.delete(roundId);
      // Also invalidate all rounds cache since it contains this round
      this.allRoundsCache = null;
    } else {
      this.roundsCache.clear();
      this.allRoundsCache = null;
    }
  }
  
  /**
   * Pre-fetch all rounds to populate cache
   * Call this when Step 3 is about to be accessed
   */
  async preloadAllRounds(): Promise<void> {
    try {
      await this.getAllInterviewRoundsSpecificToOrganization(true); // Force refresh to get latest
    } catch (error) {
      console.error('Error preloading rounds:', error);
    }
  }

  /**
   * Pre-fetch templates when Step 3 is likely to be accessed
   * Call this proactively (e.g., when Step 2 is completed)
   */
  async preloadTemplates(): Promise<void> {
    try {
      // Fetch in background, don't block
      this.getAllInterviewRoundsSpecificToOrganization(false)
        .catch(error => console.error('Background preload failed:', error));
    } catch (error) {
      console.error('Error preloading templates:', error);
    }
  }

  subscribe(event: string, callback: (...args: any[]) => void) {
    this.dispatcher.subscribe(event, callback);
  }

  unsubscribe(event: string, callback: (...args: any[]) => void) {
    this.dispatcher.unsubscribe(event, callback);
  }

  async restartInterviewSession(sessionId: string) {
    try {
      const payload = {
        sessionId: sessionId,
      };
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/interview/restart`,
        payload
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "🚀 ~ InterviewService ~ restartInterviewSession ~ error:",
        error
      );
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Failed to restart interview session",
      };
    }
  }

  async getFilterOptions() {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/interview/filter-options`
      );
      return response.data;
    } catch (error) {
      console.error("🚀 ~ InterviewService ~ getFilterOptions ~ error:", error);
      return null;
    }
  }
}

const interviewService = new InterviewService();
export default interviewService;
